import { useContext, useMemo, useRef, useState } from "react";
import { useEquipmentList } from "./useEquipmentList";
import { shouldShowHoverCard } from "./utils";
import { Button } from "components/Button";
import { Spinner, Table } from "react-bootstrap";
import { EquipmentBlockContext } from "components/utils/contexts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { EquipmentDeleteModal } from "./EquipmentDeleteModal";
import { EquipmentLabel, getCategoryIcon } from "./EquipmentLabel";
import { EquipmentListItem } from "types/EquipmentListItem";
import { isInIframe } from "components/utils/dom";
import { AppContext } from "components/App/AppContext";
import { formatJsonDate } from "components/utils/date";
import { isEmpty } from "lodash";
import { EquipmentEmptyState } from "./EquipmentEmptyState";
import { HoverCard } from "../HoverCard";
import { OverlayProps } from "@restart/ui/Overlay";

export const EquipmentList: React.FC<{
    readOnly: boolean;
    tableClassName?: string;
    tableStyle?: React.CSSProperties;
    mobileTableRowClassName?: string;
    mobileRowItemClassName?: string;
    mobileActionsClassName?: string;
}> = ({ readOnly, tableClassName, tableStyle, mobileTableRowClassName, mobileRowItemClassName, mobileActionsClassName }) => {
    const { applicationNumber, onEquipmentEdit, onEquipmentCopy, onEquipmentDelete } = useContext(EquipmentBlockContext);

    const {
        requirements: { allowEquipmentEntry },
    } = useContext(AppContext);
    const [equipmentList = [], isLoadingEquipmentList, , allowEquipmentCopy = false] = useEquipmentList(applicationNumber);
    const [showMessageModal, setShowMessageModal] = useState(false);

    const [equipment, setEquipment] = useState<EquipmentListItem>();
    const [hoveredEquipment, setHoveredEquipment] = useState<EquipmentListItem | null>(null);
    const [hoverMouseX, setHoverMouseX] = useState<number | null>(null);
    const hoveredRowRef = useRef<HTMLTableRowElement | null>(null);

    const hoverCardPopperConfig = useMemo(
        () =>
            ({
                modifiers: [
                    {
                        name: "offset",
                        options: {
                            offset: (): [number, number] => {
                                const ref = hoveredRowRef.current;
                                if (!ref || hoverMouseX == null) return [0, 0];
                                const r = ref.getBoundingClientRect();
                                const centerX = r.left + r.width / 2;
                                return [hoverMouseX - centerX, 8];
                            },
                        },
                    },
                ],
            } as OverlayProps["popperConfig"]),
        [hoverMouseX]
    );

    if (isLoadingEquipmentList) {
        return (
            <Spinner animation="border" className="align-self-center" role="status">
                <span className="visually-hidden">Loading equipment list...</span>
            </Spinner>
        );
    }

    if (!isLoadingEquipmentList && equipmentList.length === 0) {
        return <EquipmentEmptyState className={tableClassName} />;
    }

    const handleEquipmentDelete = (item: EquipmentListItem) => {
        setEquipment(item);
        setShowMessageModal(true);
    };

    return (
        <>
            <EquipmentDeleteModal
                show={showMessageModal}
                onHide={() => setShowMessageModal(false)}
                applicationNumber={applicationNumber}
                onEquipmentDelete={onEquipmentDelete}
                equipment={equipment}
            />
            <div className="equipment-list-desktop">
                <Table className={cn("application-equipment-list table-v2 align-middle m-0 w-100", tableClassName)} style={tableStyle}>
                    <thead>
                        <tr>
                            <th>Equipment</th>
                            <th>Qty</th>
                            <th>Reference ID</th>
                            <th>Date</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody className="table-group-divider">
                        {equipmentList.map((equipment) => (
                            <tr
                                key={equipment.equipid}
                                onMouseEnter={(e) => {
                                    hoveredRowRef.current = e.currentTarget;
                                    setHoveredEquipment(equipment);
                                }}
                                onMouseMove={(e) => setHoverMouseX(e.clientX)}
                                onMouseLeave={() => {
                                    setHoveredEquipment(null);
                                    setHoverMouseX(null);
                                }}
                            >
                                <td data-label="Equipment" className="pt-3 pb-3 equipment-cell">
                                    <EquipmentLabel equipment={equipment} className="pt-2 lh-sm small" />
                                </td>
                                <td data-label="Quantity">{equipment.quantity}</td>
                                <td data-label="Reference ID" className="text-break">
                                    {equipment.refid ?? "—"}
                                </td>
                                <td data-label="Date">{formatJsonDate(equipment.dateEntered, "-")}</td>
                                <td className="actions-cell w-1 text-nowrap">
                                    <EquipmentRowActions
                                        equipment={equipment}
                                        allowEquipmentEntry={!readOnly && allowEquipmentEntry}
                                        allowEquipmentCopy={!readOnly && allowEquipmentCopy}
                                        onEdit={onEquipmentEdit ?? (() => {})}
                                        onCopy={onEquipmentCopy ?? (() => {})}
                                        onDelete={handleEquipmentDelete}
                                        compact={false}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            <div className="equipment-list-mobile">
                <MobileTable
                    equipmentList={equipmentList}
                    allowEquipmentEntry={!readOnly && allowEquipmentEntry}
                    allowEquipmentCopy={!readOnly && allowEquipmentCopy}
                    onEquipmentEdit={onEquipmentEdit ?? (() => {})}
                    onEquipmentCopy={onEquipmentCopy ?? (() => {})}
                    onEquipmentDelete={handleEquipmentDelete}
                    rowClassName={mobileTableRowClassName}
                    rowItemClassName={mobileRowItemClassName}
                    actionsClassName={mobileActionsClassName}
                />
            </div>
            {hoveredEquipment != null && shouldShowHoverCard(hoveredEquipment) && hoveredRowRef.current != null && (
                <HoverCard
                    target={hoveredRowRef.current}
                    header={{
                        id: hoveredEquipment.equipid ?? hoveredEquipment.catalogNumber,
                        name: hoveredEquipment.name,
                        icon: getCategoryIcon(hoveredEquipment.imgFileLocation),
                        iconVariant: "primary",
                        subtitleLabel: "Category",
                        subtitleValue: hoveredEquipment.category,
                    }}
                    list={[
                        {
                            label: "Make",
                            value: hoveredEquipment.manufacturer,
                        },
                        {
                            label: "Model",
                            value: hoveredEquipment.model,
                        },
                        {
                            label: "Serial Number",
                            value: hoveredEquipment.serialNumber,
                        },
                    ]}
                    popperConfig={hoverCardPopperConfig}
                />
            )}
        </>
    );
};

const MobileTable: React.FC<{
    equipmentList: EquipmentListItem[];
    allowEquipmentEntry: boolean;
    allowEquipmentCopy: boolean;
    onEquipmentEdit: (equipment: EquipmentListItem) => void;
    onEquipmentCopy: (equipment: EquipmentListItem) => void;
    onEquipmentDelete: (equipment: EquipmentListItem) => void;
    rowClassName?: string;
    rowItemClassName?: string;
    actionsClassName?: string;
}> = ({
    equipmentList,
    allowEquipmentEntry,
    allowEquipmentCopy,
    onEquipmentEdit,
    onEquipmentCopy,
    onEquipmentDelete,
    rowClassName,
    rowItemClassName,
    actionsClassName,
}) => {
    return (
        <div className="gap-4 d-flex flex-column">
            {equipmentList.map((equipment) => (
                <MobileViewRow
                    key={equipment.equipid}
                    equipment={equipment}
                    allowEquipmentEntry={allowEquipmentEntry}
                    allowEquipmentCopy={allowEquipmentCopy}
                    onEquipmentEdit={onEquipmentEdit}
                    onEquipmentCopy={onEquipmentCopy}
                    onEquipmentDelete={onEquipmentDelete}
                    className={rowClassName}
                    rowItemClassName={rowItemClassName}
                    actionsClassName={actionsClassName}
                />
            ))}
        </div>
    );
};

const MobileViewRow: React.FC<{
    className?: string;
    rowItemClassName?: string;
    actionsClassName?: string;
    equipment: EquipmentListItem;
    allowEquipmentEntry: boolean;
    allowEquipmentCopy: boolean;
    onEquipmentEdit: (equipment: EquipmentListItem) => void;
    onEquipmentCopy: (equipment: EquipmentListItem) => void;
    onEquipmentDelete: (equipment: EquipmentListItem) => void;
}> = ({
    className,
    rowItemClassName,
    actionsClassName,
    equipment,
    allowEquipmentEntry,
    allowEquipmentCopy,
    onEquipmentEdit,
    onEquipmentCopy,
    onEquipmentDelete,
}) => {
    const [expanded, setExpanded] = useState(false);

    const hasExpandableAttributes = !isEmpty(equipment.manufacturer) || !isEmpty(equipment.model) || !isEmpty(equipment.serialNumber);

    return (
        <div className={cn("flex-column w-100 px-1 py-1 justify-content-center", className)}>
            <div className={cn("d-flex align-items-center justify-content-between border-bottom", rowItemClassName)}>
                <EquipmentLabel equipment={equipment} className="lh-sm py-2 small flex-grow-1" />
                {hasExpandableAttributes && (
                    <Button
                        variant="link"
                        className="text-primary"
                        onClick={() => setExpanded(!expanded)}
                        aria-expanded={expanded}
                        aria-label={expanded ? "Collapse details" : "Expand details"}
                        title={expanded ? "Collapse" : "Expand"}
                    >
                        <FontAwesomeIcon icon={["far", expanded ? "chevron-up" : "chevron-down"]} />
                    </Button>
                )}
            </div>
            <MobileViewRowItem className={rowItemClassName} label="Quantity" value={equipment.quantity.toString()} />
            <MobileViewRowItem
                className={rowItemClassName}
                label="Reference ID"
                value={equipment.refid ?? "—"}
                valueClassName="text-break"
            />
            <MobileViewRowItem
                className={rowItemClassName}
                label="Date"
                value={formatJsonDate(equipment.dateEntered, "-")}
                valueClassName="text-break"
            />
            {expanded && (
                <>
                    {equipment.manufacturer && !isEmpty(equipment.manufacturer) && (
                        <MobileViewRowItem
                            className={rowItemClassName}
                            label="Make"
                            value={equipment.manufacturer}
                            valueClassName="text-break"
                        />
                    )}
                    {equipment.model && !isEmpty(equipment.model) && (
                        <MobileViewRowItem className={rowItemClassName} label="Model" value={equipment.model} valueClassName="text-break" />
                    )}
                    {equipment.serialNumber && !isEmpty(equipment.serialNumber) && (
                        <MobileViewRowItem
                            className={rowItemClassName}
                            label="Serial number"
                            value={equipment.serialNumber}
                            valueClassName="text-break"
                        />
                    )}
                </>
            )}
            <div className={cn("d-flex align-items-center", rowItemClassName, actionsClassName)}>
                <p className="m-0 py-2 col-4 fw-bold">Actions</p>
                <EquipmentRowActions
                    equipment={equipment}
                    allowEquipmentEntry={allowEquipmentEntry}
                    allowEquipmentCopy={allowEquipmentCopy}
                    onEdit={onEquipmentEdit}
                    onCopy={onEquipmentCopy}
                    onDelete={onEquipmentDelete}
                    compact
                />
            </div>
        </div>
    );
};

const MobileViewRowItem: React.FC<{
    className?: string;
    label: string;
    value: string;
    valueClassName?: string;
}> = ({ className, label, value, valueClassName }) => {
    return (
        <div className={cn("d-flex align-items-center border-bottom", className)}>
            <p className="m-0 py-2 col-4 fw-bold">{label}</p>
            <p className={cn("m-0 ms-1 ps-2 small", valueClassName)}>{value}</p>
        </div>
    );
};

const EquipmentRowActions: React.FC<{
    equipment: EquipmentListItem;
    allowEquipmentEntry: boolean;
    allowEquipmentCopy: boolean;
    onEdit: (equipment: EquipmentListItem) => void;
    onCopy: (equipment: EquipmentListItem) => void;
    onDelete: (equipment: EquipmentListItem) => void;
    compact: boolean;
}> = ({ equipment, allowEquipmentEntry, allowEquipmentCopy, onEdit, onCopy, onDelete, compact }) => {
    const ariaDescribedby = `${equipment.equipid}-name`;
    return (
        <div
            className={cn("d-flex align-items-center gap-2", {
                "w-100 flex-row gap-1 justify-content-between pe-2": compact,
            })}
        >
            {allowEquipmentEntry ? (
                <>
                    <EditButton onClick={() => onEdit(equipment)} ariaDescribedby={ariaDescribedby} hideText={compact} />
                    {allowEquipmentCopy && (
                        <>
                            <div className="vr h-50 align-self-center" />
                            <CopyButton onClick={() => onCopy(equipment)} ariaDescribedby={ariaDescribedby} hideText={compact} />
                        </>
                    )}
                    <div className="vr h-50 align-self-center" />
                    <DeleteButton onClick={() => onDelete(equipment)} ariaDescribedby={ariaDescribedby} hideText={compact} />
                </>
            ) : (
                <ViewButton onClick={() => onEdit(equipment)} ariaDescribedby={ariaDescribedby} hideText={compact} />
            )}
        </div>
    );
};

const ViewButton = ({
    onClick,
    ariaDescribedby,
    hideText = false,
}: {
    onClick: () => void;
    ariaDescribedby: string;
    hideText?: boolean;
}) => {
    return (
        <Button
            className="d-flex align-items-center gap-2 py-0"
            variant="link"
            disabled={isInIframe()}
            onClick={onClick}
            aria-describedby={ariaDescribedby}
            aria-haspopup="dialog"
            title="View Equipment"
        >
            <FontAwesomeIcon icon={["fal", "circle-arrow-right"]} />
            {!hideText && "View"}
        </Button>
    );
};

const EditButton = ({
    onClick,
    ariaDescribedby,
    hideText = false,
}: {
    onClick: () => void;
    ariaDescribedby: string;
    hideText?: boolean;
}) => {
    return (
        <Button
            className="d-flex align-items-center gap-2 py-0"
            variant="link"
            disabled={isInIframe()}
            onClick={onClick}
            aria-describedby={ariaDescribedby}
            aria-haspopup="dialog"
            title="Edit Equipment"
        >
            <FontAwesomeIcon icon={["fal", "pen-to-square"]} />
            {!hideText && "Edit"}
        </Button>
    );
};

const DeleteButton = ({ onClick, ariaDescribedby, hideText }: { onClick: () => void; ariaDescribedby: string; hideText?: boolean }) => {
    return (
        <Button
            className="d-flex align-items-center gap-2 py-0 text-danger"
            variant="link"
            disabled={isInIframe()}
            onClick={onClick}
            aria-describedby={ariaDescribedby}
            aria-haspopup="dialog"
            title="Delete Equipment"
        >
            <FontAwesomeIcon icon={["fal", "trash-can"]} />
            {!hideText && "Delete"}
        </Button>
    );
};

const CopyButton = ({
    onClick,
    ariaDescribedby,
    hideText = false,
}: {
    onClick: () => void;
    ariaDescribedby: string;
    hideText?: boolean;
}) => {
    return (
        <Button
            className="d-flex align-items-center gap-2 py-0"
            variant="link"
            onClick={onClick}
            aria-describedby={ariaDescribedby}
            aria-haspopup="dialog"
            title="Copy Equipment"
        >
            <FontAwesomeIcon icon={["fal", "clone"]} />
            {!hideText && "Copy"}
        </Button>
    );
};
